import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search } from 'lucide-react';
import type { RequisiteWorkloadProfile } from '../../api/dashboard';
import { displayBankName } from '../../utils/displayBank';

function profileKey(profile: RequisiteWorkloadProfile): string {
  if (profile.key) return profile.key;
  const source = profile.source === 'qr' ? 'qr' : 'bank';
  return `${source}:${profile.id}`;
}

function ProfileList({
  profiles,
  selected,
  emptyLabel,
  onToggle,
}: {
  profiles: RequisiteWorkloadProfile[];
  selected: Set<string>;
  emptyLabel: string;
  onToggle: (key: string) => void;
}) {
  if (profiles.length === 0) {
    return <div className="px-2 py-4 text-center text-[11px] text-slate-400">{emptyLabel}</div>;
  }
  return (
    <>
      {profiles.map((profile) => {
        const key = profileKey(profile);
        const checked = selected.has(key);
        const bank = displayBankName(profile.bank_name);
        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(key)}
            className={`flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition ${
              checked ? 'bg-sky-50' : 'hover:bg-slate-50'
            }`}
          >
            <span
              className={`mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                checked
                  ? 'border-sky-500 bg-sky-500 text-white'
                  : 'border-slate-300 bg-white text-transparent'
              }`}
            >
              <Check className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[11px] font-semibold text-slate-800">
                {profile.name}
              </span>
              <span className="block truncate text-[10px] text-slate-400">
                {[profile.group_name, bank].filter(Boolean).join(' · ') || '—'}
              </span>
            </span>
          </button>
        );
      })}
    </>
  );
}

export function RequisiteProfileFilter({
  profiles,
  selectedKeys,
  onChange,
}: {
  profiles: RequisiteWorkloadProfile[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelBox, setPanelBox] = useState<{ top: number; left: number } | null>(null);

  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const allKeys = useMemo(() => profiles.map(profileKey), [profiles]);

  const ownProfiles = useMemo(
    () => profiles.filter((p) => (p.kind || 'own') !== 'counterparty'),
    [profiles],
  );
  const contraProfiles = useMemo(
    () => profiles.filter((p) => p.kind === 'counterparty'),
    [profiles],
  );

  const filterList = useCallback(
    (list: RequisiteWorkloadProfile[]) => {
      const q = query.trim().toLowerCase();
      if (!q) return list;
      return list.filter((profile) => {
        const hay = `${profile.name} ${profile.group_name} ${profile.bank_name} ${displayBankName(profile.bank_name)}`.toLowerCase();
        return hay.includes(q);
      });
    },
    [query],
  );

  const filteredOwn = useMemo(() => filterList(ownProfiles), [filterList, ownProfiles]);
  const filteredContra = useMemo(() => filterList(contraProfiles), [filterList, contraProfiles]);

  const buttonLabel =
    selectedKeys.length === 0
      ? 'Реквизиты'
      : selectedKeys.length === profiles.length
        ? 'Все реквизиты'
        : `Реквизиты · ${selectedKeys.length}`;

  const syncPosition = useCallback(() => {
    const trigger = rootRef.current;
    if (!trigger || !open) return;
    const rect = trigger.getBoundingClientRect();
    const width = 520;
    const pad = 8;
    const left = Math.min(Math.max(pad, rect.right - width), window.innerWidth - width - pad);
    const top = Math.min(rect.bottom + 6, window.innerHeight - pad);
    setPanelBox({ top, left });
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    syncPosition();
  }, [open, selectedKeys.length, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => syncPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (key: string) => {
    if (selected.has(key)) {
      onChange(selectedKeys.filter((item) => item !== key));
      return;
    }
    onChange([...selectedKeys, key]);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex h-6 items-center rounded-md border px-2 text-[11px] font-semibold transition ${
          selectedKeys.length > 0
            ? 'border-sky-300 bg-sky-50 text-sky-800'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        {buttonLabel}
      </button>

      {open && panelBox
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed z-[80] w-[520px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
              style={{ top: panelBox.top, left: panelBox.left }}
            >
              <div className="border-b border-slate-100 p-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Поиск реквизита"
                    className="h-8 w-full rounded-lg border border-slate-200 bg-slate-50 pl-7 pr-2 text-xs text-slate-800 outline-none focus:border-sky-300 focus:bg-white"
                  />
                </div>
                <div className="mt-1.5 flex gap-1">
                  <button
                    type="button"
                    className="h-6 flex-1 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => onChange(allKeys)}
                  >
                    Все
                  </button>
                  <button
                    type="button"
                    className="h-6 flex-1 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => onChange([])}
                  >
                    Сбросить
                  </button>
                </div>
              </div>
              {contraProfiles.length === 0 ? (
                <div className="vnd-scroll max-h-72 min-w-0 overflow-y-auto p-1.5">
                  <ProfileList
                    profiles={filteredOwn}
                    selected={selected}
                    emptyLabel="Нет профилей"
                    onToggle={toggle}
                  />
                </div>
              ) : (
                <div className="grid max-h-72 grid-cols-2 divide-x divide-slate-100">
                  <div className="vnd-scroll min-w-0 overflow-y-auto p-1.5">
                    <div className="sticky top-0 z-[1] bg-white px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      Наши · {filteredOwn.length}
                    </div>
                    <ProfileList
                      profiles={filteredOwn}
                      selected={selected}
                      emptyLabel="Нет наших реквизитов"
                      onToggle={toggle}
                    />
                  </div>
                  <div className="vnd-scroll min-w-0 overflow-y-auto p-1.5">
                    <div className="sticky top-0 z-[1] bg-white px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      Контрагенты · {filteredContra.length}
                    </div>
                    <ProfileList
                      profiles={filteredContra}
                      selected={selected}
                      emptyLabel="Нет реквизитов контрагентов"
                      onToggle={toggle}
                    />
                  </div>
                </div>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
